import type {
  TelemetryAuthContext,
  TelemetryClient,
  TelemetryConfig,
  TrackMetricAggregate,
  TrackPageViewParams,
} from '@klareospace/pharos-lib-front-utils';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';

const ANONYMOUS_USER_ID = 'anonymous';

const NO_OP_CLIENT: TelemetryClient = {
  setAuthContext: (): void => {},
  trackEvent: (): void => {},
  trackException: (): void => {},
  trackMetric: (): void => {},
  trackPageView: (): void => {},
  trackTrace: (): void => {},
};

/**
 * Create a telemetry client backed by Azure Application Insights.
 *
 * All automatic Microsoft telemetry collection is disabled — only explicit
 * track calls are sent. When `connectionString` is empty, a no-op client is
 * returned so local-dev builds generate no network traffic and no console noise.
 *
 * Every item automatically carries common customDimensions: appName, appVersion,
 * environment, platform, tenantId, userId (defaults to 'anonymous' until
 * setAuthContext is called).
 */
export function createTelemetryClient(config: TelemetryConfig): TelemetryClient {
  if (!config.connectionString) {
    return NO_OP_CLIENT;
  }

  const appInsights = new ApplicationInsights({
    config: {
      autoTrackPageVisitTime: false,
      connectionString: config.connectionString,
      disableAjaxTracking: true,
      disableExceptionTracking: true,
      disableFetchTracking: true,
      enableAutoRouteTracking: false,
      enableUnhandledPromiseRejectionTracking: false,
    },
  });

  try {
    appInsights.loadAppInsights();
  } catch {
    // Initialization failure must not crash the app — fall back to no-op client.
    return NO_OP_CLIENT;
  }

  // Mutable auth state — updated by setAuthContext.
  let currentTenantId = '';
  let currentUserId = ANONYMOUS_USER_ID;

  // Merge common custom dimensions into every telemetry envelope.
  appInsights.addTelemetryInitializer((envelope: ITelemetryItem): boolean => {
    try {
      if (!envelope.baseData) {
        envelope.baseData = {};
      }
      const existing = (envelope.baseData['properties'] as Record<string, string> | undefined) ?? {};
      envelope.baseData['properties'] = {
        ...existing,
        appName: config.appName,
        appVersion: config.appVersion,
        environment: config.environment,
        platform: 'web',
        tenantId: currentTenantId,
        userId: currentUserId,
      };
    } catch {
      // Swallowed — initializer must never throw.
    }
    return true;
  });

  return {
    setAuthContext: (context: TelemetryAuthContext | null): void => {
      try {
        if (context) {
          currentTenantId = context.tenantId;
          currentUserId = context.userId;
        } else {
          currentTenantId = '';
          currentUserId = ANONYMOUS_USER_ID;
        }
      } catch {
        // Swallowed — telemetry must never cause an app error.
      }
    },

    trackEvent: (name: string, properties?: Record<string, string>): void => {
      try {
        appInsights.trackEvent({ name }, properties);
      } catch {
        // Swallowed.
      }
    },

    trackException: (error: unknown, extraDimensions?: Record<string, string>): void => {
      try {
        const exception = error instanceof Error ? error : new Error(String(error));
        appInsights.trackException({ exception }, extraDimensions);
      } catch {
        // Swallowed.
      }
    },

    trackMetric: (
      name: string,
      value: number,
      properties?: Record<string, string>,
      aggregate?: TrackMetricAggregate,
    ): void => {
      try {
        appInsights.trackMetric(
          { average: value, max: aggregate?.max, min: aggregate?.min, name, sampleCount: aggregate?.count },
          properties,
        );
      } catch {
        // Swallowed.
      }
    },

    trackPageView: ({ name, uri }: TrackPageViewParams): void => {
      try {
        appInsights.trackPageView({ name, uri });
      } catch {
        // Swallowed.
      }
    },

    trackTrace: (message: string, properties?: Record<string, string>): void => {
      try {
        appInsights.trackTrace({ message }, properties);
      } catch {
        // Swallowed.
      }
    },
  };
}
